// src/pages/admin/AdminCompanySetting.js
import React, { useEffect, useState } from "react";
import { useParams } from 'react-router-dom';
import AdminPageHeader from "../../components/common/AdminPageHeader";
import { useMainCompany } from '../../context/MainCompanyContext';
import { useRoleTheme } from "../../context/RoleThemeContext";
import { Save } from "lucide-react";
import api from "../../api/axios";
import { formatCurrency, sanitizeNumberInput } from "../../utils/format";
import FloatingNumber from "../../components/input/FloatingNumber";

// Modern Toast Component
function Toast({ message, visible }) {
  if (!visible) return null;
  return (
    <div className="fixed bottom-6 right-6 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-lg shadow-xl z-50 flex items-center gap-3 animate-in slide-in-from-bottom-2 duration-300">
      <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <span className="font-medium">{message}</span>
    </div>
  );
}

const OPTIONS = [
  "Seller Costs",
  "Short Sale",
  "FHA/VA/USDA Loans",
  "Buyer Costs",
  "Refinance Costs",
  "Conventional Costs",
  "Title/Escrow Costs",
];

const TEMPLATE_KEY_MAP = {
  "Seller Costs": ["seller_closing_costs_section1", "seller_closing_costs_section2"],
  "Short Sale": ["shortsale_closing_costs_section1", "shortsale_closing_costs_section2"],
  "FHA/VA/USDA Loans": ["fha_closing_costs", "va_closing_costs", "usda_closing_costs"],
  "Buyer Costs": [
    "buyer_tenure_section",
    "buyer_taxes_insurance_section",
    "buyer_prepaid_section",
    "buyer_payments_section",
    "buyer_closing_costs_section",
  ],
  "Refinance Costs": ["refinance_closing_costs_section1", "refinance_closing_costs_section2"],
  "Conventional Costs": ["conventional_adjustable_section", "conventional_closing_costs_section"],
  "seller_othercosts": ["payments_to_buyer"],
};

export default function AdminCompanySetting() {
  const [activeOption, setActiveOption] = useState(OPTIONS[0]);
  const [template, setTemplate] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ message: "", visible: false });

  // Fee data states
  const [titleFees, setTitleFees] = useState([]);
  const [escrowFees, setEscrowFees] = useState([]);

  // Row edit mode states - track which rows are in edit mode
  const [editingTitleFeeRows, setEditingTitleFeeRows] = useState(new Set());
  const [editingEscrowFeeRows, setEditingEscrowFeeRows] = useState(new Set());

  // Upload states
  const [titleFeesUploading, setTitleFeesUploading] = useState(false);
  const [escrowFeesUploading, setEscrowFeesUploading] = useState(false);

  const { selectedMain } = useMainCompany();
  const { roleTheme } = useRoleTheme();
  const params = useParams();
  const primaryBtnClasses = `${roleTheme?.colors?.buttons?.primary || 'bg-gradient-to-r from-blue-600 to-blue-700'} text-white`;
  const focusRingClass = "focus:ring-2 focus:ring-[var(--role-border-primary)] focus:ring-offset-2";
  const primaryBtnClassesFull = `${primaryBtnClasses} shadow-sm hover:shadow-md transform hover:scale-[1.02] transition-all duration-200 ${focusRingClass}`;
  const urlMainId = params?.maincompanyid;
  const urlCompanyId = params?.companyid;


  const mainCompanyId = Number(urlMainId) || selectedMain;

  /** ----------------- Add / Remove Fee Row ----------------- */
  const addFeeRow = (sectionKey, leth) => {
    setTemplate((prev) => {
      const updated = { ...prev };
      const cnt = leth + 1;
      const newRow = {
        sr_no: cnt,
        key: "Other" + cnt,
        label: "Other",
        value: "0.00",
        type: "Flat Fee",
        unit: "$",
        rate: 0,
        Cash: "0.00",
        lock: "N",
        direction: "CHARGE",
      };
      updated[sectionKey] = [...updated[sectionKey], newRow];
      return updated;
    });
  };

  const removeFeeRow = (sectionKey, idx) => {
    setTemplate((prev) => {
      const updated = { ...prev };
      updated[sectionKey] = updated[sectionKey].filter((_, i) => i !== idx);
      return updated;
    });
  };

  const updateFee = (sectionKey, idx, key, value, subKey = null) => {
    setTemplate((prev) => {
      const updated = { ...prev };

      if (subKey) {
        // handle nested object like seller_othercosts.payments_to_buyer
        const parent = { ...updated[sectionKey] };
        const arr = [...parent[subKey]];
        arr[idx] = { ...arr[idx], [key]: value };
        parent[subKey] = arr;
        updated[sectionKey] = parent;
      } else {
        // handle normal flat array
        updated[sectionKey][idx] = { ...updated[sectionKey][idx], [key]: value };
      }

      return updated;
    });
  };



  const updateDisclaimer = (optionKey, value) => {
    setTemplate((prev) => {
      const updated = { ...prev };
      updated.extra[optionKey] = {
        ...updated.extra[optionKey],
        disclaimer_message: value,
      };
      return updated;
    });
  };

  const saveDisclaimer = async (optionKey) => {
    try {
      const resp = await api.put(`/admin/global-company-template/disclaimer/${optionKey}/${mainCompanyId}/${selectedCompanyId}`, {
        disclaimer_message: template.extra[optionKey].disclaimer_message,
      });
      if (resp.data.status === "success") {
        setToast({ message: "Disclaimer saved successfully!", visible: true });
        setTimeout(() => setToast({ message: "", visible: false }), 3000);
      }
    } catch (err) {
      console.error(err);
      setToast({ message: "Error saving disclaimer.", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    }
  };

  const validateSection = (sectionKey) => {
    const fees = template[sectionKey];
    if (!fees) return false;

    for (let i = 0; i < fees.length; i++) {
      const fee = fees[i];
      if (!fee.label || fee.label.trim() === "") {
        setToast({ message: `Label required in ${sectionKey} (Row ${i + 1})`, visible: true });
        return false;
      }
      // normalize: remove commas, trim spaces
      // (?? not || — fee.value can legitimately be the number 0, which is falsy
      // but not "missing"; || would coerce it to "" and fail validation below)
      const rawValue = String(fee.value ?? "").replace(/,/g, "").trim();

      if (rawValue === "" || isNaN(rawValue) || parseFloat(rawValue) < 0) {
        setToast({ message: `Amount must be > 0 in ${sectionKey} (Row ${i + 1})`, visible: true });
        return false;
      }
    }
    return true;
  };

  const saveSection = async (sectionKey) => {
    if (!validateSection(sectionKey)) return;

    try {
      const resp = await api.put(`/admin/global-company-template/section/${sectionKey}/${mainCompanyId}/${selectedCompanyId}`, {
        [sectionKey]: template[sectionKey],
      });
      if (resp.data.status === "success") {
        setToast({ message: "Section saved successfully!", visible: true });
        setTimeout(() => setToast({ message: "", visible: false }), 3000);
      }
    } catch (err) {
      console.error(err);
      setToast({ message: "Error saving section.", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    }
  };

  // Load companies whenever mainCompanyId changes
  useEffect(() => {
    // guards against StrictMode's double-invoke (and fast filter changes) committing
    // state from a run that's since been superseded/unmounted
    const ignoreRef = { current: false };

    const loadCompanies = async () => {
      if (!mainCompanyId) return; // nothing to fetch

      setLoading(true);
      try {
        const resp = await api.get(`/admin/sub-companies/dropdown/${mainCompanyId}`);
        if (ignoreRef.current) return;
        const body = resp.data;
        if (body.status === "success" && body.data.data.length > 0) {
          const list = body.data.data;
          setCompanies(list);

          let initialCompanyId = 0;

          if (urlCompanyId) {
            // Case 1: company id in URL → trust it
            initialCompanyId = urlCompanyId;
          } else {
            // Case 2 & 3: no company id → use first from list
            initialCompanyId = list[0].id;
          }

          setSelectedCompanyId(initialCompanyId);
          await loadTemplate(initialCompanyId, ignoreRef);
        }
      } catch (err) {
        if (!ignoreRef.current) console.error("Error loading companies", err);
      } finally {
        if (!ignoreRef.current) setLoading(false);
      }
    };

    loadCompanies();
    return () => { ignoreRef.current = true; };
    // loadTemplate is stable and called from other effects; omit to avoid unnecessary reruns
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainCompanyId, urlCompanyId]);

  const loadTemplate = async (companyId, ignoreRef = null) => {
    if (!mainCompanyId || !companyId) return;
    setLoading(true);
    try {
      // Load template data
      const templateResp = await api.get(
        `/admin/global-company-template/${mainCompanyId}/${companyId}`
      );
      if (ignoreRef?.current) return;
      if (templateResp.data.status === "success" && templateResp.data.data.length > 0) {
        setTemplate(templateResp.data.data[0]);
      }

      // Load title fees data
      try {
        const titleFeesResp = await api.get(
          `/admin/global-company-template/title-fees/${mainCompanyId}/${companyId}`
        );
        if (ignoreRef?.current) return;
        if (titleFeesResp.data.status === "success") {
          setTitleFees(titleFeesResp.data.data || []);
        }
      } catch (titleFeesErr) {
        if (!ignoreRef?.current) {
          console.log("Title fees not found, using empty array");
          setTitleFees([]);
        }
      }

      // Load escrow fees data
      try {
        const escrowFeesResp = await api.get(
          `/admin/global-company-template/escrow-fees/${mainCompanyId}/${companyId}`
        );
        if (ignoreRef?.current) return;
        if (escrowFeesResp.data.status === "success") {
          setEscrowFees(escrowFeesResp.data.data || []);
        }
      } catch (escrowFeesErr) {
        if (!ignoreRef?.current) {
          console.log("Escrow fees not found, using empty array");
          setEscrowFees([]);
        }
      }
    } catch (err) {
      if (!ignoreRef?.current) console.error("Error loading template", err);
    } finally {
      if (!ignoreRef?.current) setLoading(false);
    }
  };

  const setSelectedMain = (id) => {
    setSelectedCompanyId(id);
    loadTemplate(id);
    setActiveOption(OPTIONS[0]);
  };

  // Row-wise update functions
  const updateTitleFee = async (feeId, updatedFee) => {
    try {
      const resp = await api.put(
        `/admin/global-company-template/title-fee/${mainCompanyId}/${selectedCompanyId}/${feeId}`,
        updatedFee
      );

      if (resp.data.status === "success") {
        setToast({ message: "Title fee updated successfully!", visible: true });
        setTimeout(() => setToast({ message: "", visible: false }), 3000);
        return true;
      } else {
        setToast({ message: "Failed to update title fee.", visible: true });
        setTimeout(() => setToast({ message: "", visible: false }), 3000);
        return false;
      }
    } catch (err) {
      console.error("Update error:", err);
      setToast({ message: "Error updating title fee.", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
      return false;
    }
  };

  const updateEscrowFee = async (feeId, updatedFee) => {
    try {
      const resp = await api.put(
        `/admin/global-company-template/escrow-fee/${mainCompanyId}/${selectedCompanyId}/${feeId}`,
        updatedFee
      );

      if (resp.data.status === "success") {
        setToast({ message: "Escrow fee updated successfully!", visible: true });
        setTimeout(() => setToast({ message: "", visible: false }), 3000);
        return true;
      } else {
        setToast({ message: "Failed to update escrow fee.", visible: true });
        setTimeout(() => setToast({ message: "", visible: false }), 3000);
        return false;
      }
    } catch (err) {
      console.error("Update error:", err);
      setToast({ message: "Error updating escrow fee.", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
      return false;
    }
  };

  // Download sample file functions
  const downloadTitleFeeSample = async () => {
    try {
      const response = await api.get('/sample/title-fee', {
        responseType: 'blob', // Important for file downloads
      });

      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Upload Title Insurance Fee.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setToast({ message: "Title fee sample downloaded successfully!", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    } catch (err) {
      console.error("Download error:", err);
      setToast({ message: "Error downloading title fee sample.", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    }
  };

  const downloadEscrowFeeSample = async () => {
    try {
      const response = await api.get('/sample/escrow-fee', {
        responseType: 'blob', // Important for file downloads
      });

      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Upload Escrow Fee.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setToast({ message: "Escrow fee sample downloaded successfully!", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    } catch (err) {
      console.error("Download error:", err);
      setToast({ message: "Error downloading escrow fee sample.", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    }
  };

  // Upload functions
  const uploadTitleFees = async (file) => {
    if (!file || !mainCompanyId || !selectedCompanyId) return;

    setTitleFeesUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const resp = await api.put(
        `/admin/global-company-template/title-fees/${mainCompanyId}/${selectedCompanyId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (resp.data.status === "success") {
        setToast({ message: "Title Fee data uploaded successfully!", visible: true });
        setTimeout(() => setToast({ message: "", visible: false }), 3000);
        // Reload the data
        loadTemplate(selectedCompanyId);
      } else {
        setToast({ message: "Failed to upload Title Fee data.", visible: true });
        setTimeout(() => setToast({ message: "", visible: false }), 3000);
      }
    } catch (err) {
      console.error("Upload error:", err);
      setToast({ message: "Error uploading Title Fee data.", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    } finally {
      setTitleFeesUploading(false);
    }
  };

  const uploadEscrowFees = async (file) => {
    if (!file || !mainCompanyId || !selectedCompanyId) return;

    setEscrowFeesUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const resp = await api.put(
        `/admin/global-company-template/escrow-fees/${mainCompanyId}/${selectedCompanyId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      if (resp.data.status === "success") {
        setToast({ message: "Escrow Fee data uploaded successfully!", visible: true });
        setTimeout(() => setToast({ message: "", visible: false }), 3000);
        // Reload the data
        loadTemplate(selectedCompanyId);
      } else {
        setToast({ message: "Failed to upload Escrow Fee data.", visible: true });
        setTimeout(() => setToast({ message: "", visible: false }), 3000);
      }
    } catch (err) {
      console.error("Upload error:", err);
      setToast({ message: "Error uploading Escrow Fee data.", visible: true });
      setTimeout(() => setToast({ message: "", visible: false }), 3000);
    } finally {
      setEscrowFeesUploading(false);
    }
  };


  if (loading || !template) {
    return (
      <div className="p-4 space-y-6 bg-gray-50 min-h-screen">
        <AdminPageHeader title="Manage Company Settings" />
        <div className="bg-white rounded-lg border border-gray-200 p-12 shadow-sm">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="relative">
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-[var(--role-border-light)]"></div>
              <div className="animate-spin rounded-full h-16 w-16 border-4 border-transparent border-t-blue-600 absolute top-0 left-0"></div>
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Company Settings</h3>
              <p className="text-gray-600">Please wait while we load the configuration data...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }


  const sectionKeys = TEMPLATE_KEY_MAP[activeOption];

  return (
    <div className="p-4 space-y-6 bg-gray-50 min-h-screen">
      <AdminPageHeader title="Manage Company Settings" />

      {/* Company Selection Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="max-w-md">
          <label className="block text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
            Select Company
          </label>
          <div className="relative">
            <select
              value={selectedCompanyId}
              onChange={(e) => setSelectedMain(Number(e.target.value))}
              className="w-full pl-4 pr-10 py-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors bg-white appearance-none"
            >
              <option value="0">-- Select Company --</option>
              {companies.map((company) => (
                <option key={company.id} value={company.id}>
                  {company.company_name}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
          <p className="mt-2 text-sm text-gray-600">
            Choose a company to configure its fee templates and settings.
          </p>
        </div>
      </div>

      {/* Configuration Sections */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Fee Configuration</h2>
          <p className="text-sm text-gray-600">Configure closing costs and fees for different transaction types.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
          {OPTIONS.map((opt) => (
            <button
              key={opt}
              onClick={() => setActiveOption(opt)}
              className={`px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200 text-center ${activeOption === opt
                  ? `${primaryBtnClasses} shadow-lg transform scale-[1.02] border-2 border-[var(--role-border-primary)]`
                  : "bg-gray-50 text-gray-700 hover:bg-gray-100 hover:shadow-md border-2 border-transparent hover:border-gray-200"
                }`}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      {/* Render sections dynamically */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Render fee tables for all except Title/Escrow */}
        {activeOption !== "Title/Escrow Costs" && activeOption !== "Commissions" &&
          sectionKeys.map((sectionKey) => (
            <div
              key={sectionKey}
              className={`bg-white shadow-md rounded-xl p-4 border
    ${sectionKey === "buyer_closing_costs_section" 
                  ? "md:col-span-2"   // full width
                  : "md:col-span-1"   // half width
                }
  `}
            >

              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg">
                    {sectionKey.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </h3>
                </div>
                <button
                  className={`inline-flex items-center gap-2 px-4 py-2.5 ${primaryBtnClassesFull} rounded-lg`}
                  onClick={() => saveSection(sectionKey)}
                >
                  <Save size={16} /> Save
                </button>
              </div>


              {/* Fees Table */}
              <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 rounded-lg">
                <table className="w-full text-sm table-auto border-collapse bg-white">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wide">Label</th>
                      {[
                        "seller_closing_costs_section2",
                        "shortsale_closing_costs_section2",
                        "buyer_closing_costs_section",
                        "refinance_closing_costs_section2",
                      ].includes(sectionKey) && (
                          <th className="px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wide w-[100px]">Type</th>
                        )}

                      <th className="px-4 py-3 text-center font-semibold text-gray-700 uppercase tracking-wide w-[80px]">Amount</th>
                      {[
                        "fha_closing_costs",
                        "usda_closing_costs",
                        "va_closing_costs",
                      ].includes(sectionKey) && (
                          <th className="px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wide w-[100px]">Payer</th>
                        )}
                      {(sectionKey === "buyer_closing_costs_section" ||
                        sectionKey === "buyer_payments_section") && (
                          <th className="px-4 py-3 text-center font-semibold text-gray-700 uppercase tracking-wide w-[80px]">Apply to Cash</th>
                        )}
                      {(sectionKey === "buyer_closing_costs_section" ||
                        sectionKey === "seller_closing_costs_section2" ||
                        sectionKey === "shortsale_closing_costs_section2") && (
                          <th className="px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wide w-[80px]">Cash</th>
                        )}
                      <th className="px-4 py-3 text-center font-semibold text-gray-700 uppercase tracking-wide w-[40px]">Lock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {template[sectionKey].map((fee, idx) => (
                      <tr key={idx} className="hover:bg-blue-50 transition-colors duration-150">
                        {/* Label */}
                        <td className="px-4 py-3 border-b border-gray-100 font-medium text-gray-900">
                          <div className="flex items-center justify-between gap-2">
                            <input
                              type="text"
                              value={fee.label || ""}
                              onChange={(e) => updateFee(sectionKey, idx, "label", e.target.value)}
                              className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
                            />
                            {["seller_closing_costs_section1", "shortsale_closing_costs_section1", "buyer_payments_section"].includes(sectionKey) &&
                              fee.rate !== undefined && (
                                <FloatingNumber
                                  type="text"
                                  value={fee.rate || "0"}
                                  onChange={(e) => updateFee(sectionKey, idx, "rate", e.target.value)}
                                  className="w-12 border rounded px-1 py-1 text-sm text-right"
                                />
                              )}

                            {(sectionKey === "seller_closing_costs_section2" || sectionKey === "shortsale_closing_costs_section2" || sectionKey === "buyer_closing_costs_section" || sectionKey === "refinance_closing_costs_section2") &&
                              idx >= 10 && (
                                <button
                                  onClick={() => removeFeeRow(sectionKey, idx)}
                                  className="text-red-500 text-xs hover:underline"
                                >
                                  ✕
                                </button>
                              )}

                          </div>
                        </td>


                        {/* Type / Payer / Blank Column */}
                        {/* Type */}
                        {[
                          "seller_closing_costs_section2",
                          "shortsale_closing_costs_section2",
                          "buyer_closing_costs_section",
                          "refinance_closing_costs_section2",
                        ].includes(sectionKey) && fee.type !== undefined && (
                            <td className="px-2 py-1 border w-[100px]">
                              <select
                                value={fee.type || "Flat Fee"}
                                onChange={(e) => updateFee(sectionKey, idx, "type", e.target.value)}
                                className="w-27 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
                              >
                                <option>Flat Fee</option>
                                <option>% Sales Price</option>
                                <option>% Loan</option>
                              </select>
                            </td>
                          )}

                        {/* Amount */}
                        <td className="px-2 py-1 border w-[80px]">
                          <div className="flex items-center gap-2">
                            {[
                              "buyer_closing_costs_section",
                              "seller_closing_costs_section2",
                              "shortsale_closing_costs_section2",
                              "refinance_closing_costs_section2",
                            ].includes(sectionKey) && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    updateFee(
                                      sectionKey,
                                      idx,
                                      "direction",
                                      fee.direction === "CREDIT" ? "CHARGE" : "CREDIT"
                                    )
                                  }
                                  title={
                                    fee.direction === "CREDIT"
                                      ? "Credit — subtracts from the total"
                                      : "Charge — adds to the total"
                                  }
                                  className={`w-6 h-6 flex-shrink-0 flex items-center justify-center rounded border text-xs font-bold ${
                                    fee.direction === "CREDIT"
                                      ? "border-green-500 text-green-600 bg-green-50"
                                      : "border-gray-300 text-gray-500"
                                  }`}
                                >
                                  {fee.direction === "CREDIT" ? "−" : "+"}
                                </button>
                              )}
                            <span className="text-gray-500 text-sm whitespace-nowrap">{fee.unit}</span>
                            <FloatingNumber
                              type="text"
                              value={
                                fee.value_type === "rate" ||
                                  fee.value_type === "month" ||
                                  fee.value_type === "year"
                                  ? fee.value ?? ""
                                  : fee.value !== undefined && fee.value !== null
                                    ? fee.value
                                    : "0.00"
                              }
                              onChange={(e) => updateFee(sectionKey, idx, "value", sanitizeNumberInput(e.target.value))}
                              onBlur={(e) => {
                                if (
                                  fee.value_type !== "rate" &&
                                  fee.value_type !== "month" &&
                                  fee.value_type !== "year"
                                ) {
                                  updateFee(sectionKey, idx, "value", formatCurrency(e.target.value));
                                }
                              }}
                              className="w-20 flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
                            />
                          </div>
                        </td>

                        {[
                          "fha_closing_costs",
                          "usda_closing_costs",
                          "va_closing_costs",
                        ].includes(sectionKey) && fee.payer !== undefined && fee.payer !== "factor" && fee.label !== "Origination Factor" && (
                            <td className="px-2 py-1 border w-[100px]">
                              <select
                                value={fee.payer || "Buyer"}
                                onChange={(e) => updateFee(sectionKey, idx, "payer", e.target.value)}
                                className="w-27 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
                              >
                                <option>Buyer</option>
                                <option>Seller</option>
                              </select>
                            </td>
                          )}

                        {[
                          "fha_closing_costs",
                          "usda_closing_costs",
                          "va_closing_costs",
                        ].includes(sectionKey) && fee.payer !== undefined && fee.payer === "factor" && (
                            <td className="px-2 py-1 border w-[100px]"></td>
                          )}


                        {/* Apply checkbox */}
                        {(sectionKey === "buyer_closing_costs_section" ||
                          sectionKey === "buyer_payments_section") && (
                            <td className="px-2 py-1 border text-center w-[120px]">
                              {(sectionKey === "buyer_closing_costs_section" || fee.apply !== undefined) && (
                                <input
                                  type="checkbox"
                                  checked={fee.apply === "Y"}
                                  onChange={(e) =>
                                    updateFee(sectionKey, idx, "apply", e.target.checked ? "Y" : "N")
                                  }
                                />
                              )}
                            </td>
                          )}

                        {(sectionKey === "buyer_closing_costs_section" ||
                          sectionKey === "seller_closing_costs_section2" ||
                          sectionKey === "shortsale_closing_costs_section2") && (
                            <td className="px-2 py-1 border text-center w-[80px]">
                              <FloatingNumber
                                type="text"
                                value={
                                  fee.value_type === "rate" ||
                                    fee.value_type === "month" ||
                                    fee.value_type === "year"
                                    ? fee.Cash ?? ""
                                    : fee.Cash !== undefined && fee.Cash !== null
                                      ? fee.Cash
                                      : "0.00"
                                }
                                onChange={(e) => updateFee(sectionKey, idx, "Cash", sanitizeNumberInput(e.target.value))}
                                onBlur={(e) => {
                                  if (
                                    fee.value_type !== "rate" &&
                                    fee.value_type !== "month" &&
                                    fee.value_type !== "year"
                                  ) {
                                    updateFee(sectionKey, idx, "Cash", formatCurrency(e.target.value));
                                  }
                                }}
                                className="w-20 border rounded px-1 py-1 text-sm"
                              />
                            </td>
                          )}

                        {/* Lock */}
                        <td className="px-2 py-1 text-center border w-[20px]">
                          <input
                            type="checkbox"
                            checked={fee.lock === "Y"}
                            onChange={(e) =>
                              updateFee(sectionKey, idx, "lock", e.target.checked ? "Y" : "N")
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>

                  {/* Add Row button after 10 rows */}
                  {(sectionKey === "seller_closing_costs_section2" || sectionKey === "shortsale_closing_costs_section2" || sectionKey === "buyer_closing_costs_section" || sectionKey === "refinance_closing_costs_section2") &&
                    template[sectionKey].length >= 10 && template[sectionKey].length < 15 && (
                      <tfoot>
                        <tr>
                          <td colSpan="100%" className="px-2 py-2 text-center border">
                            <button
                              onClick={() => addFeeRow(sectionKey, template[sectionKey].length)}
                              className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                            >
                              + Add Row
                            </button>
                          </td>
                        </tr>
                      </tfoot>
                    )}
                </table>
              </div>

            </div>
          ))}


        {/* Render Title/Escrow Costs separately */}
        {activeOption === "Title/Escrow Costs" && (
          <>
            <div className="bg-white shadow-md rounded-xl p-4 border md:col-span-2">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg">
                    Title / Escrow Costs – Paid By Settings
                  </h3>
                </div>
                <button
                  className={`inline-flex items-center gap-2 px-4 py-2.5 ${primaryBtnClassesFull} rounded-lg`}
                  onClick={async () => {
                    try {
                      const resp = await api.put(
                        `/admin/global-company-template/disclaimer/Paid by/${mainCompanyId}/${selectedCompanyId}`,
                        { "Paid by": template.extra["Paid by"] }
                      );
                      if (resp.data.status === "success") {
                        setToast({ message: "Paid By settings saved!", visible: true });
                        setTimeout(() => setToast({ message: "", visible: false }), 3000);
                      }
                    } catch (err) {
                      console.error(err);
                      setToast({ message: "Error saving Paid By settings.", visible: true });
                      setTimeout(() => setToast({ message: "", visible: false }), 3000);
                    }
                  }}
                >
                  <Save size={16} /> Save
                </button>
              </div>

              <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 rounded-lg">
                <table className="w-full text-sm table-auto border-collapse bg-white">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wide">Fee Label</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700 uppercase tracking-wide w-[120px]">Paid By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(template.extra["Paid by"] || {}).map(([label, payer], idx) => (
                      <tr key={idx} className="hover:bg-blue-50 transition-colors duration-150">
                        <td className="px-4 py-3 border-b border-gray-100 font-medium text-gray-900">{label}</td>
                        <td className="px-4 py-3 border-b border-gray-100 text-center">
                          <select
                            value={payer}
                            onChange={(e) =>
                              setTemplate((prev) => ({
                                ...prev,
                                extra: {
                                  ...prev.extra,
                                  "Paid by": {
                                    ...prev.extra["Paid by"],
                                    [label]: e.target.value,
                                  },
                                },
                              }))
                            }
                            className="border rounded px-2 py-1 text-sm"
                          >
                            <option value="Split">Split</option>
                            <option value="Seller">Seller</option>
                            <option value="Buyer">Buyer</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Total Escrow Fee Section */}
            <div className="bg-white shadow-md rounded-xl p-4 border md:col-span-1">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg">
                    Total Escrow Fee
                  </h3>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={downloadEscrowFeeSample}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transform hover:scale-[1.02] transition-all duration-200 focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download Sample
                  </button>
                  <label className={`inline-flex items-center gap-2 px-4 py-2.5 ${primaryBtnClasses} text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transform hover:scale-[1.02] transition-all duration-200 ${focusRingClass} cursor-pointer`}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    {escrowFeesUploading ? "Uploading..." : "Upload Escrow Fee"}
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          uploadEscrowFees(file);
                        }
                        // Reset the input
                        e.target.value = '';
                      }}
                      className="hidden"
                      disabled={escrowFeesUploading}
                    />
                  </label>
                </div>
              </div>

              <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 rounded-lg">
                <table className="w-full text-sm table-auto border-collapse bg-white">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wide">FROMVALUE</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wide">TOVALUE</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wide">Escrow Fee</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wide">Refinance Fee</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {escrowFees.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                          No escrow fee data available. Upload a file or contact administrator.
                        </td>
                      </tr>
                    ) : (
                      escrowFees.map((fee, index) => {
                        console.log('Rendering escrow fee row:', index, 'fee.id:', fee.id, 'isEditing:', editingEscrowFeeRows.has(fee.id), 'editingRows:', Array.from(editingEscrowFeeRows));
                        return (
                      <tr key={index} className="hover:bg-indigo-50 transition-colors duration-150">
                        <td className="px-4 py-3 border-b border-gray-100 font-medium text-gray-900">
                          ${fee.fromValue.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 border-b border-gray-100 font-medium text-gray-900">
                          ${fee.toValue.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 border-b border-gray-100">
                          <div className="flex items-center">
                            <span className="text-gray-500 mr-1">$</span>
                            <FloatingNumber
                              value={fee.escrowFee || ""}
                              onChange={(e) => {
                                const updatedFees = [...escrowFees];
                                updatedFees[index].escrowFee = e.target.value;
                                setEscrowFees(updatedFees);
                              }}
                              onBlur={() => {
                                console.log('Escrow fee onBlur - fee.id:', fee.id, 'isEditing:', editingEscrowFeeRows.has(fee.id));
                              }}
                              className="w-20 flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
                              disabled={!editingEscrowFeeRows.has(fee.id)}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3 border-b border-gray-100">
                          <div className="flex items-center">
                            <span className="text-gray-500 mr-1">$</span>
                            <FloatingNumber
                              value={fee.refinanceFee || ""}
                              onChange={(e) => {
                                const updatedFees = [...escrowFees];
                                updatedFees[index].refinanceFee = e.target.value;
                                setEscrowFees(updatedFees);
                              }}
                              onBlur={() => {
                                console.log('Escrow fee onBlur - fee.id:', fee.id, 'isEditing:', editingEscrowFeeRows.has(fee.id));
                              }}
                              className="w-20 flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
                              disabled={!editingEscrowFeeRows.has(fee.id)}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3 border-b border-gray-100 text-center">
                          <button
                            className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                              editingEscrowFeeRows.has(fee.id)
                                ? 'bg-green-600 hover:bg-green-700 text-white'
                                : primaryBtnClasses
                            }`}
                            onClick={async () => {
                              if (editingEscrowFeeRows.has(fee.id)) {
                                // Save mode - save the data and exit edit mode
                                console.log('Saving escrow fee row:', fee.id, fee);
                                const success = await updateEscrowFee(fee.id, {
                                  escrowFee: fee.escrowFee,
                                  refinanceFee: fee.refinanceFee
                                });

                                // Exit edit mode regardless of success (UI will be refreshed on failure)
                                setEditingEscrowFeeRows(prev => {
                                  const newSet = new Set(prev);
                                  newSet.delete(fee.id);
                                  return newSet;
                                });

                                if (!success) {
                                  loadTemplate(selectedCompanyId);
                                }
                              } else {
                                // Edit mode - enter edit mode for this row only
                                console.log('Entering edit mode for escrow fee row:', fee.id);
                                setEditingEscrowFeeRows(prev => new Set([...prev, fee.id]));
                              }
                            }}
                          >
                            {editingEscrowFeeRows.has(fee.id) ? (
                              <>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Save
                              </>
                            ) : (
                              <>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    );
                    })
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Title Fee Section */}
            <div className="bg-white shadow-md rounded-xl p-4 border md:col-span-1">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg">
                    Title Fee
                  </h3>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={downloadTitleFeeSample}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transform hover:scale-[1.02] transition-all duration-200 focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Download Sample
                  </button>
                  <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transform hover:scale-[1.02] transition-all duration-200 focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 cursor-pointer">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    {titleFeesUploading ? "Uploading..." : "Upload Title Fee"}
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          uploadTitleFees(file);
                        }
                        // Reset the input
                        e.target.value = '';
                      }}
                      className="hidden"
                      disabled={titleFeesUploading}
                    />
                  </label>
                </div>
              </div>

              <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 rounded-lg">
                <table className="w-full text-sm table-auto border-collapse bg-white">
                  <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wide">Upto Price</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wide">OWNER FEE</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wide">LENDER FEE</th>
                      <th className="px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wide">Refinance Fee</th>
                      <th className="px-4 py-3 text-center font-semibold text-gray-700 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {titleFees.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                          No title fee data available. Upload a file or contact administrator.
                        </td>
                      </tr>
                    ) : (
                      titleFees.map((fee, index) => {
                        console.log('Rendering title fee row:', index, 'fee.id:', fee.id, 'isEditing:', editingTitleFeeRows.has(fee.id), 'editingRows:', Array.from(editingTitleFeeRows));
                        return (
                      <tr key={index} className="hover:bg-purple-50 transition-colors duration-150">
                      {/*  <td className="px-4 py-3 border-b border-gray-100 font-medium text-gray-900">
                          ${fee.fromValue.toLocaleString()}
                        </td> */}
                        <td className="px-4 py-3 border-b border-gray-100 font-medium text-gray-900">
                          ${fee.toValue.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 border-b border-gray-100">
                          <div className="flex items-center">
                            <span className="text-gray-500 mr-1">$</span>
                            <FloatingNumber
                              value={fee.ownerFee || ""}
                              onChange={(e) => {
                                const updatedFees = [...titleFees];
                                updatedFees[index].ownerFee = e.target.value;
                                setTitleFees(updatedFees);
                              }}
                              onBlur={() => {
                                console.log('Title fee onBlur - fee.id:', fee.id, 'isEditing:', editingTitleFeeRows.has(fee.id));
                              }}
                              className="w-20 flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
                              disabled={!editingTitleFeeRows.has(fee.id)}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3 border-b border-gray-100">
                          <div className="flex items-center">
                            <span className="text-gray-500 mr-1">$</span>
                            <FloatingNumber
                              value={fee.lenderFee || ""}
                              onChange={(e) => {
                                const updatedFees = [...titleFees];
                                updatedFees[index].lenderFee = e.target.value;
                                setTitleFees(updatedFees);
                              }}
                              onBlur={() => {
                                console.log('Title fee onBlur - fee.id:', fee.id, 'isEditing:', editingTitleFeeRows.has(fee.id));
                              }}
                              className="w-20 flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
                              disabled={!editingTitleFeeRows.has(fee.id)}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3 border-b border-gray-100">
                          <div className="flex items-center">
                            <span className="text-gray-500 mr-1">$</span>
                            <FloatingNumber
                              value={fee.refinanceFee || ""}
                              onChange={(e) => {
                                const updatedFees = [...titleFees];
                                updatedFees[index].refinanceFee = e.target.value;
                                setTitleFees(updatedFees);
                              }}
                              onBlur={() => {
                                console.log('Title fee onBlur - fee.id:', fee.id, 'isEditing:', editingTitleFeeRows.has(fee.id));
                              }}
                              className="w-20 flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[var(--role-border-primary)] focus:border-[var(--role-border-primary)] transition-colors"
                              disabled={!editingTitleFeeRows.has(fee.id)}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3 border-b border-gray-100 text-center">
                          <button
                            className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                              editingTitleFeeRows.has(fee.id)
                                ? 'bg-green-600 hover:bg-green-700 text-white'
                                : primaryBtnClasses
                            }`}
                            onClick={async () => {
                              if (editingTitleFeeRows.has(fee.id)) {
                                // Save mode - save the data and exit edit mode
                                console.log('Saving title fee row:', fee.id, fee);
                                const success = await updateTitleFee(fee.id, {
                                  ownerFee: fee.ownerFee,
                                  lenderFee: fee.lenderFee,
                                  refinanceFee: fee.refinanceFee
                                });

                                // Exit edit mode regardless of success (UI will be refreshed on failure)
                                setEditingTitleFeeRows(prev => {
                                  const newSet = new Set(prev);
                                  newSet.delete(fee.id);
                                  return newSet;
                                });

                                if (!success) {
                                  loadTemplate(selectedCompanyId);
                                }
                              } else {
                                // Edit mode - enter edit mode for this row only
                                console.log('Entering edit mode for title fee row:', fee.id);
                                setEditingTitleFeeRows(prev => new Set([...prev, fee.id]));
                              }
                            }}
                          >
                            {editingTitleFeeRows.has(fee.id) ? (
                              <>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                Save
                              </>
                            ) : (
                              <>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>


          </>
        )}

        {activeOption === OPTIONS[0] && template?.seller_othercosts?.payments_to_buyer && (
          <div className="bg-white shadow-md rounded-xl p-4 border md:col-span-2">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <h3 className="font-semibold text-gray-900 text-lg">
                  Commissions - Payments to Buyer
                </h3>
              </div>
              <button
                className={`inline-flex items-center gap-2 px-4 py-2.5 ${primaryBtnClasses} text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transform hover:scale-[1.02] transition-all duration-200 ${focusRingClass}`}
                onClick={() => saveSection("seller_othercosts")}
              >
                <Save size={16} /> Save
              </button>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm table-auto border-collapse">
                <thead className="bg-gray-100 text-gray-700 uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 uppercase tracking-wide">Label</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700 uppercase tracking-wide w-[80px]">Rate</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700 uppercase tracking-wide w-[80px]">Amount</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700 uppercase tracking-wide w-[60px]">Lock</th>
                  </tr>
                </thead>
                <tbody>
                  {template.seller_othercosts.payments_to_buyer
                    .filter((fee) => fee.key !== "days_interest")
                    .map((fee, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                        {/* Label */}
                        <td className="px-2 py-1 border">
                          <input
                            type="text"
                            value={fee.label || ""}
                            onChange={(e) =>
                              updateFee("seller_othercosts", idx, "label", e.target.value, "payments_to_buyer")
                            }
                            className="w-full border rounded px-1 py-1 text-sm"
                          />
                        </td>

                        {/* Rate */}
                        <td className="px-2 py-1 border text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <FloatingNumber
                              type="text"
                              value={fee.rate || ""}
                              onChange={(e) =>
                                updateFee("seller_othercosts", idx, "rate", e.target.value, "payments_to_buyer")
                              }
                              className="w-20 border rounded px-1 py-1 text-sm text-right"
                            />
                            <span className="text-gray-500 text-sm">%</span>
                          </div>
                        </td>

                        {/* Amount */}
                        <td className="px-2 py-1 border text-center">
                          <div className="flex items-center gap-2 justify-center">
                            <span className="text-gray-500 text-sm">{fee.unit || "$"}</span>
                            <FloatingNumber
                              type="text"
                              value={fee.value || ""}
                              onChange={(e) =>
                                updateFee(
                                  "seller_othercosts",
                                  idx,
                                  "value",
                                  sanitizeNumberInput(e.target.value),
                                  "payments_to_buyer"
                                )
                              }
                              onBlur={(e) =>
                                updateFee(
                                  "seller_othercosts",
                                  idx,
                                  "value",
                                  formatCurrency(e.target.value),
                                  "payments_to_buyer"
                                )
                              }
                              className="w-24 border rounded px-1 py-1 text-sm text-right"
                            />
                          </div>
                        </td>

                        {/* Lock */}
                        <td className="px-2 py-1 border text-center">
                          <input
                            type="checkbox"
                            checked={fee.lock === "Y"}
                            onChange={(e) =>
                              updateFee(
                                "seller_othercosts",
                                idx,
                                "lock",
                                e.target.checked ? "Y" : "N",
                                "payments_to_buyer"
                              )
                            }
                          />
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

      </div>

      {/* Toast */}
      <Toast message={toast.message} visible={toast.visible} />

      {/* Disclaimer Editor */}
      {["Seller Costs", "Buyer Costs", "Short Sale", "Refinance Costs"].includes(activeOption) && (
        <div className="bg-white shadow-md rounded-xl p-4 border md:col-span-2">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 text-lg">Disclaimer for {activeOption}</h3>
            </div>
            <button className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold ${primaryBtnClassesFull} rounded-lg`} onClick={() => saveDisclaimer(activeOption)}>
              <Save size={16} /> Save Disclaimer
            </button>
          </div>
          <textarea
            value={template.extra[activeOption]?.disclaimer_message || ""}
            onChange={(e) => updateDisclaimer(activeOption, e.target.value)}
            className="w-full border border-gray-300 rounded-lg px-4 py-3 text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors resize-vertical"
            rows={4}
            placeholder="Enter disclaimer text for this fee configuration..."
          />
        </div>
      )}

    </div>
  );
}
